import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Users, Loader2, X, LogOut } from 'lucide-react';
import { useOrganizationStore } from '../stores/organizationStore';
import { useAuthStore } from '../stores/authStore';
import { organizationApi } from '../services/api';
import { showToast } from '../components/common/Toast';
import { useTranslation } from 'react-i18next';

export const SelectOrganizationPage: React.FC = () => {
  const navigate = useNavigate();
  const { createOrganization, fetchOrganizations } = useOrganizationStore();
  const { checkAuth, logout } = useAuthStore();
  const { t } = useTranslation();
  
  const [mode, setMode] = useState<'choice' | 'create' | 'join'>('choice');
  const [orgName, setOrgName] = useState('');
  const [joinUuid, setJoinUuid] = useState('');
  const [lookupResult, setLookupResult] = useState<{ id: string; name: string } | null>(null);
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [actionError, setActionError] = useState('');

  const handleCreate = async () => {
    if (!orgName.trim()) return;
    setCreating(true);
    setActionError('');
    try {
      const org = await createOrganization(orgName.trim());
      if (org) {
        await fetchOrganizations();
        await checkAuth();
        showToast(t('organizations.success.created', { name: org.name }), 'success');
        navigate('/', { replace: true });
      } else {
        setActionError(t('organizations.error.create'));
      }
    } catch {
      setActionError(t('organizations.error.create'));
    }
    setCreating(false);
  };

  const handleLookup = async () => {
    if (!joinUuid.trim()) return;
    setLookupLoading(true);
    setLookupError('');
    setLookupResult(null);
    try {
      const response = await organizationApi.lookup(joinUuid.trim());
      if (response.success && response.data) {
        setLookupResult(response.data as { id: string; name: string });
      } else {
        setLookupError(t('organizations.error.notFound'));
      }
    } catch {
      setLookupError(t('organizations.error.notFound'));
    }
    setLookupLoading(false);
  };

  const handleJoin = async () => {
    if (!lookupResult) return;
    setJoining(true);
    setActionError('');
    try {
      const response = await organizationApi.requestJoin(lookupResult.id);
      if (response.success) {
        showToast(
          `Join request sent to "${lookupResult.name}". It is pending admin approval.`,
          'success'
        );
        setLookupResult(null);
        setJoinUuid('');
      } else {
        setActionError(t('organizations.error.requestJoin'));
      }
    } catch {
      setActionError(t('organizations.error.requestJoin'));
    }
    setJoining(false);
  };

  const resetToChoice = () => {
    setMode('choice');
    setOrgName('');
    setJoinUuid('');
    setLookupResult(null);
    setLookupError('');
    setActionError('');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Welcome to TaskFly
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
            You don't have any organizations yet. Create one to get started or join an existing one.
          </p>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>

        {/* Error display */}
        {actionError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
            {actionError}
          </div>
        )}

        {/* Choice Mode */}
        {mode === 'choice' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <button
              onClick={() => setMode('create')}
              className="w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-left"
            >
              <div className="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                <Plus className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100">Create Organization</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Start a new organization and invite others</div>
              </div>
            </button>

            <button
              onClick={() => setMode('join')}
              className="w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-left"
            >
              <div className="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100">Join Organization</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Enter an organization ID to join</div>
              </div>
            </button>
          </div>
        )}

        {/* Create Mode */}
        {mode === 'create' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Create Organization
              </h2>
              <button
                onClick={resetToChoice}
                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
{t('organizations.page.organizationName')}
                </label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder={t('organizations.page.myOrganization')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                />
              </div>

              <button
                onClick={handleCreate}
                disabled={creating || !orgName.trim()}
                className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Organization
              </button>
            </div>
          </div>
        )}

        {/* Join Mode */}
        {mode === 'join' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Join Organization
              </h2>
              <button
                onClick={resetToChoice}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {!lookupResult ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
{t('organizations.page.idOrCode')}
                    </label>
                    <input
                      type="text"
                      value={joinUuid}
                      onChange={(e) => setJoinUuid(e.target.value)}
                      placeholder={t('organizations.page.enterId')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                    />
                  </div>

                  {lookupError && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                      {lookupError}
                    </div>
                  )}

                  <button
                    onClick={handleLookup}
                    disabled={lookupLoading || !joinUuid.trim()}
                    className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {lookupLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Find Organization
                  </button>
                </>
              ) : (
                <>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{lookupResult.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Organization</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => { setLookupResult(null); setJoinUuid(''); }}
                      className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
{t('common.cancel')}
                    </button>
                    <button
                      onClick={handleJoin}
                      disabled={joining}
                      className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {joining && <Loader2 className="w-4 h-4 animate-spin" />}
                      Join
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectOrganizationPage;
